import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, CheckCircle2, AlertTriangle, BookOpen, Calendar, Lock, RefreshCw, Play, ExternalLink, ListOrdered, ChevronDown } from "lucide-react";
import { useApiData, fetchApi } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";

// Odkazy v dokumentu: video odkazy (Loom/YouTube) jako klikací tlačítko s názvem,
// ostatní odkazy normálně, vždy v novém tabu. Sdíleno přes ReactMarkdown components.
export function DocLink({ href, children }) {
  const url = String(href || "");
  const isVideo = /loom\.com\/share|youtu\.be\/|youtube\.com\/watch/.test(url);
  if (isVideo) {
    // Text odkazu bez uvodniho 🎥 (ikonu dodava tlacitko samo).
    let label = React.Children.toArray(children).map((c) => (typeof c === "string" ? c : "")).join("").replace(/^🎥\s*/, "").trim();
    // Fallback: kdyby text byl jen URL (nepojmenovane video), misto skarede adresy "Přehrát video".
    if (!label || /^https?:\/\//.test(label)) label = "Přehrát video";
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="not-prose inline-flex items-center gap-2 bg-brand-50 hover:bg-brand-100 active:bg-brand-200 text-brand-800 border border-brand-200 rounded-xl px-3 py-2 my-0.5 text-sm font-semibold no-underline transition-colors max-w-full"
      >
        <span className="w-6 h-6 rounded-full bg-brand-600 text-white flex items-center justify-center shrink-0">
          <Play className="w-3.5 h-3.5 ml-0.5" fill="currentColor" />
        </span>
        <span className="truncate">{label}</span>
        <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-60" />
      </a>
    );
  }
  return <a href={url} target="_blank" rel="noopener noreferrer">{children}</a>;
}

// === Interaktivní obsah dokumentu (4.6.2026) ===
// Z markdownu vytáhneme nadpisy (h1/h2), vyrenderujeme je jako klikací pill boxy
// a kliknutí plynule sroluje na příslušnou sekci. Statický "Obsah" blok z Notion
// exportu se z těla skryje (nahrazuje ho právě tento interaktivní).
const slugifyHeading = (s) => String(s || "")
  .toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "sekce";

// Odstraní statickou sekci Obsah (nadpis nebo tučný řádek "Obsah") až po další nadpis
// + osamocený emoji řádek na začátku (ikona stránky z Notion exportu).
export function stripStaticToc(md) {
  return String(md || "")
    .replace(/^(?:#{1,3}\s*)?(?:\*\*)?\s*(?:📋\s*)?Obsah:?\s*(?:\*\*)?\s*$[\s\S]*?(?=^#{1,3}\s)/m, "")
    .replace(/^\s*[^\w#>\-*\d\s][^\n]{0,8}\n+(?=#)/, "");
}

// Hierarchický obsah: h1–h3 (kromě titulu dokumentu a "Obsah"), top úroveň = nejmenší
// level v dokumentu, hlubší nadpisy jsou podsekce předchozí top položky.
export function extractToc(md) {
  const lines = String(md || "").split("\n");
  const flat = [];
  let inCode = false;
  for (const line of lines) {
    if (/^```/.test(line)) { inCode = !inCode; continue; }
    if (inCode) continue;
    const m = line.match(/^(#{1,3})\s+(.+)$/);
    if (!m) continue;
    const text = m[2].replace(/\*\*/g, "").trim();
    if (/^obsah:?$/i.test(text.replace(/^📋\s*/, ""))) continue;
    flat.push({ level: m[1].length, text, id: slugifyHeading(text) });
  }
  if (flat.length > 1) flat.shift(); // titul dokumentu je už v hlavičce stránky
  if (flat.length === 0) return [];
  const topLevel = Math.min(...flat.map((h) => h.level));
  const tree = [];
  for (const h of flat) {
    if (h.level === topLevel || tree.length === 0) tree.push({ ...h, children: [] });
    else tree[tree.length - 1].children.push(h);
  }
  return tree;
}

// === Grafické zpracování dokumentu (4.6.2026) ===
// Sekce jsou vizuálně oddělené: h1 = velký nadpis s gradientovou linkou,
// h2 = barevný sekční banner (fialový pruh + podklad), h3 = odrážka s tečkou.
// Citace (callouty z Notionu) = fialová info karta, oddělovače = jemný gradient.
const headingText = (children) =>
  React.Children.toArray(children).map((c) => (typeof c === "string" ? c : c?.props?.children || "")).join("");

function H1({ children }) {
  return (
    <h1 id={slugifyHeading(headingText(children))} className="scroll-mt-20 not-prose mt-10 first:mt-0 mb-5">
      <span className="block text-2xl sm:text-3xl font-black text-slate-900 leading-tight">{children}</span>
      <span className="block h-1 w-24 mt-2 rounded-full bg-gradient-to-r from-brand-600 to-plum-400" />
    </h1>
  );
}
function H2({ children }) {
  return (
    <h2
      id={slugifyHeading(headingText(children))}
      className="scroll-mt-20 not-prose mt-9 mb-4 px-4 py-3 rounded-r-xl rounded-l-lg border-l-4 border-brand-500 bg-gradient-to-r from-brand-50 via-brand-50/60 to-transparent text-lg sm:text-xl font-bold text-slate-900 leading-snug"
    >
      {children}
    </h2>
  );
}
function H3({ children }) {
  return (
    <h3 id={slugifyHeading(headingText(children))} className="scroll-mt-20 not-prose mt-6 mb-2.5 flex items-center gap-2 text-base sm:text-lg font-bold text-slate-800">
      <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />
      <span>{children}</span>
    </h3>
  );
}
function Callout({ children }) {
  return (
    <blockquote className="not-prose my-5 bg-brand-50 border border-brand-200 border-l-4 border-l-brand-500 rounded-r-xl px-4 py-3 text-[15px] leading-relaxed text-slate-700 [&_p]:m-0 [&_p+p]:mt-2">
      {children}
    </blockquote>
  );
}
function Divider() {
  return (
    <div className="not-prose my-8 flex items-center gap-3" role="separator">
      <span className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-slate-200" />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
      <span className="flex-1 h-px bg-gradient-to-l from-transparent via-slate-300 to-slate-200" />
    </div>
  );
}
export const MD_COMPONENTS = { a: DocLink, h1: H1, h2: H2, h3: H3, blockquote: Callout, hr: Divider };

// === Obsah dokumentu — svislý, hierarchický, rozbalovací (4.6.2026 v2) ===
// Sekce pod sebou (čitelné i na mobilu), podsekce v dropdownu, klik sroluje na sekci.
export function DocToc({ bodyMd }) {
  const tree = useMemo(() => extractToc(stripStaticToc(bodyMd)), [bodyMd]);
  const [openIds, setOpenIds] = useState(() => new Set());
  if (tree.length < 2) return null;
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const toggle = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 sm:px-5 py-3 bg-gradient-to-r from-brand-50 to-transparent border-b border-brand-100 flex items-center gap-2">
        <ListOrdered className="w-4 h-4 text-brand-600" />
        <span className="text-sm font-bold uppercase tracking-wider text-brand-800">Obsah</span>
        <span className="text-xs text-slate-400 ml-auto">{tree.length} sekcí</span>
      </div>
      <div className="divide-y divide-slate-100">
        {tree.map((t, i) => {
          const hasKids = t.children.length > 0;
          const isOpen = openIds.has(t.id);
          return (
            <div key={`${t.id}-${i}`}>
              <div className="flex items-stretch">
                <button
                  type="button"
                  onClick={() => scrollTo(t.id)}
                  className="flex-1 min-w-0 flex items-center gap-2.5 px-4 sm:px-5 py-2.5 text-left hover:bg-brand-50 active:bg-brand-100 transition-colors"
                >
                  <span className="w-1 self-stretch rounded-full bg-brand-300 shrink-0" />
                  <span className="text-sm sm:text-[15px] font-semibold text-slate-800 leading-snug">{t.text}</span>
                </button>
                {hasKids && (
                  <button
                    type="button"
                    onClick={() => toggle(t.id)}
                    className="shrink-0 px-3 sm:px-4 flex items-center text-slate-400 hover:text-brand-700 hover:bg-brand-50 transition-colors"
                    title={isOpen ? "Sbalit podsekce" : `Zobrazit podsekce (${t.children.length})`}
                  >
                    <span className="text-[11px] font-bold tabular-nums mr-1">{t.children.length}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
              {hasKids && isOpen && (
                <div className="pb-1.5 bg-slate-50/60">
                  {t.children.map((c, j) => (
                    <button
                      key={`${c.id}-${j}`}
                      type="button"
                      onClick={() => scrollTo(c.id)}
                      className="w-full flex items-center gap-2 pl-10 sm:pl-12 pr-4 py-2 text-left hover:bg-brand-50 active:bg-brand-100 transition-colors"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-400 shrink-0" />
                      <span className="text-sm text-slate-600 leading-snug">{c.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// === DOKUMENT DETAIL — reader s scroll progress + sign-off ===
// - Scroll progress bar nahoře (fixed) — kolik % textu už projel
// - Markdown se vykreslí přes react-markdown + GFM (tables, checklists)
// - Tlačítko "Potvrzuji" se odemkne až po doscrollování (≥ 95%) — žádné auto-confirm
// - Klik = POST /api/dokumenty action=sign → uloží souhlas + zavře task
export default function TrenerDokumentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const email = user?.email || "";
  const trenerName = user?.linkedTrener || user?.name || "";
  const url = `/api/dokumenty?slug=${encodeURIComponent(slug)}&email=${encodeURIComponent(email)}`;
  const { data, loading, error, refetch } = useApiData(url);

  const doc = data?.doc;
  const status = data?.status || "unread";
  const signedVersion = data?.signedVersion || 0;

  const [progress, setProgress] = useState(0); // 0..100
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedNow, setSignedNow] = useState(false);
  const [signError, setSignError] = useState("");
  const [binding, setBinding] = useState(false); // závazné zaškrtnutí před podpisem
  const articleRef = useRef(null);

  // Sleduj scroll: progress = scrollTop / maxScroll (clamped)
  useEffect(() => {
    if (!articleRef.current) return;
    const calc = () => {
      const docEl = document.documentElement;
      const winH = window.innerHeight;
      const docH = docEl.scrollHeight - winH;
      if (docH <= 0) {
        setProgress(100);
        setScrolledToEnd(true);
        return;
      }
      const sc = docEl.scrollTop;
      const pct = Math.min(100, Math.max(0, Math.round((sc / docH) * 100)));
      setProgress(pct);
      if (pct >= 95) setScrolledToEnd(true);
    };
    calc();
    window.addEventListener("scroll", calc, { passive: true });
    window.addEventListener("resize", calc);
    return () => {
      window.removeEventListener("scroll", calc);
      window.removeEventListener("resize", calc);
    };
  }, [doc?.body_md]);

  const isSigned = signedNow || status === "signed";
  const needsSign = !isSigned;
  const isOutdated = status === "outdated";

  const handleSign = async () => {
    if (!doc) return;
    setSigning(true);
    setSignError("");
    try {
      await fetchApi("/api/dokumenty", {
        method: "POST",
        body: JSON.stringify({
          action: "sign",
          slug: doc.slug,
          version: doc.version,
          trener_email: email,
          trener_jmeno: trenerName,
        }),
      });
      setSignedNow(true);
      // Po krátké pauze refetch (aby měl data čerstvé pro případný návrat)
      setTimeout(() => refetch(), 800);
    } catch (e) {
      setSignError(e.message || "Potvrzení selhalo");
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Scroll progress bar — fixed nahoře pod hlavičkou Layoutu */}
      <div className="sticky top-0 z-20 -mx-3 sm:-mx-4 -mt-3 sm:-mt-4 px-3 sm:px-4 pt-2 pb-2 bg-slate-50/80 backdrop-blur border-b border-slate-200">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <Link
            to="/trener/dokumenty"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 shadow-sm hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200 active:scale-95 transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Zpět na dokumenty
          </Link>
          <div className="text-xs text-slate-500 tabular-nums font-semibold">{progress}%</div>
        </div>
        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-150 ${isSigned ? "bg-emerald-500" : "bg-brand-600"}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stavy */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">Chyba: {error}</div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-10 text-sm text-slate-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Načítám dokument…
        </div>
      )}

      {doc && (
        <>
          {/* Hlavička dokumentu */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-0.5">{doc.kategorie || "Ostatní"}</div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight">{doc.nazev}</h1>
                {doc.popis && <div className="text-sm text-slate-600 mt-1.5">{doc.popis}</div>}
                <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-2 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />Aktualizováno {doc.updated_at}</span>
                  <span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" />
                  <span>verze {doc.version}</span>
                  {doc.deadline_dni && (
                    <>
                      <span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" />
                      <span>deadline {doc.deadline_dni} dní od uploadu</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Status banner */}
            {status === "signed" && !signedNow && (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-sm text-emerald-800 inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Tento dokument jsi přečetl/a (verze {signedVersion}). Stačí jen znovu projít.
              </div>
            )}
            {status === "outdated" && (
              <div className="mt-3 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 text-sm text-orange-800 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Dokument byl aktualizován (potvrdil/a jsi verzi {signedVersion}, aktuální je verze {doc.version}). Přečti si změny a potvrď znovu.
              </div>
            )}
          </div>

          {/* Interaktivní obsah — svislý seznam sekcí s rozbalovacími podsekcemi */}
          {doc.body_md && <DocToc bodyMd={doc.body_md} />}

          {/* Tělo dokumentu — markdown rendering */}
          <article
            ref={articleRef}
            className="bg-white border border-slate-200 rounded-xl p-5 sm:p-7 shadow-sm prose prose-slate prose-base max-w-none
              prose-headings:font-bold prose-headings:text-slate-900
              prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
              prose-a:text-brand-700 prose-a:underline-offset-2
              prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-rose-700 prose-code:font-medium prose-code:before:content-none prose-code:after:content-none
              prose-pre:bg-slate-900 prose-pre:text-slate-100
              prose-blockquote:border-l-brand-400 prose-blockquote:text-slate-700
              prose-table:text-sm
              prose-th:bg-slate-100 prose-th:font-semibold
              prose-li:marker:text-brand-400 prose-strong:text-slate-900
              prose-p:leading-relaxed prose-li:leading-relaxed
              prose-img:rounded-xl"
          >
            {doc.body_md ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{stripStaticToc(doc.body_md)}</ReactMarkdown>
            ) : (
              <div className="text-sm text-slate-400 italic">Tento dokument zatím nemá obsah.</div>
            )}
          </article>

          {/* Sign-off CTA */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
            {signedNow && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 inline-flex items-center gap-2 w-full">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <div>
                  <b>Potvrzeno!</b> Verze {doc.version} je teď v pořádku. Související úkol byl uzavřen.
                </div>
              </div>
            )}
            {!signedNow && (
              <>
                <div className="text-sm text-slate-700 mb-3">
                  {isSigned && !isOutdated
                    ? "Tento dokument už je potvrzený. Pokud sis ho jen znovu prošel/a, není třeba nic dělat."
                    : "Pokud jsi dokument přečetl/a a porozuměl/a, klikni níže. Tím se uloží potvrzení a uzavře se úkol v Kanbanu."}
                </div>
                {signError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-700 mb-3">
                    {signError}
                  </div>
                )}
                {/* Závazná formulace — povinné zaškrtnutí před podpisem. */}
                <label className="flex items-start gap-2.5 mb-3 p-3 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={binding}
                    onChange={(e) => setBinding(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-emerald-600"
                  />
                  <span className="text-sm text-slate-700">
                    Potvrzuji, že jsem dokument přečetl/a, porozuměl/a a <strong>zavazuji se ho dodržovat</strong>.
                  </span>
                </label>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={handleSign}
                    disabled={signing || (needsSign && !scrolledToEnd) || !binding}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                      (needsSign && !scrolledToEnd) || signing || !binding
                        ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                    }`}
                  >
                    {signing
                      ? <><RefreshCw className="w-4 h-4 animate-spin" /> Ukládám…</>
                      : (needsSign && !scrolledToEnd
                        ? <><Lock className="w-4 h-4" /> Doscrolluj na konec dokumentu</>
                        : <><CheckCircle2 className="w-4 h-4" /> {isSigned && !isOutdated ? "Potvrdit znovu" : "Potvrzuji že jsem přečetl/a a porozuměl/a"}</>)
                    }
                  </button>
                  {needsSign && !scrolledToEnd && (
                    <span className="text-xs text-slate-500">Zbývá {Math.max(0, 95 - progress)}%</span>
                  )}
                  <button
                    onClick={() => navigate("/trener/dokumenty")}
                    className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2"
                  >
                    Zpět na seznam
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
